import _ from 'lodash';
import { getParams } from '../../utils/location';
import { LOGIN_TYPES } from '../LoginView';
import { AuthDialogInterface } from './AuthDialogInterface';

/**
 * Missing Email Case - Highest Priority
 * User is logged in but missing email address
 */
export class MissingEmailCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user } = context;
    return user.loggedIn && !_.get(user.userDetails, 'data.email');
  }

  viewToShow(context) {
    return 'missing-email';
  }

  getCaseName() {
    return 'MissingEmailCase';
  }
}

/**
 * Privacy Consent Case - High Priority
 * User needs to provide privacy consent
 */
export class PrivacyConsentCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user, appStyles } = context;
    const loyaltyProfileData = _.get(user.loyaltyProfile, 'data');
    console.log('loyaltyProfileData', user.loyaltyProfile);
    console.log('PrivacyConsentCase', user.loggedIn, appStyles.gdprConsentEnabled, _.isEmpty(_.get(loyaltyProfileData, 'privacyConsent')), _.get(loyaltyProfileData, 'privacyConsent.status') !== 'APPROVED', _.get(loyaltyProfileData, 'privacyConsent'));

    console.log("returning", user.loggedIn && 
           appStyles.gdprConsentEnabled &&
           (_.isEmpty(_.get(loyaltyProfileData, 'privacyConsent')) ||
           _.get(loyaltyProfileData, 'privacyConsent.status') !== 'APPROVED'));

    return user.loggedIn && 
           appStyles.gdprConsentEnabled && user.loyaltyProfile.data &&
           (_.isEmpty(_.get(loyaltyProfileData, 'privacyConsent')) ||
           _.get(loyaltyProfileData, 'privacyConsent.status') !== 'APPROVED');
  }

  viewToShow(context) {
    return 'consent';
  }

  getCaseName() {
    return 'PrivacyConsentCase';
  }
}

/**
 * Verify Code Case - High Priority
 * User needs to verify their phone number
 */
export class VerifyCodeCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user } = context;
    return user.showVerifyView;
  }

  viewToShow(context) {
    return 'verify';
  }

  getCaseName() {
    return 'VerifyCodeCase';
  }
}

/**
 * Signup Case - Medium Priority
 * User needs to sign up (based on URL or login type)
 */
export class SignupCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user, location, app } = context;
    const params = getParams(location);
    
    return (
      !user.loggedIn && (
      user.authMode === 'signup' ||
      _.includes(location.pathname, 'signup') ||
      params.openSignup ||
      app.loginType === LOGIN_TYPES.GIFT_CARD ||
      app.isSignupPopupMode ||
      app.isEmbed ||
      Boolean(app.inStoreSignupBranchId))
    );
  }

  viewToShow(context) {
    return 'signup';
  }


  getCaseName() {
    return 'SignupCase';
  }
}

/**
 * Login Case - Medium Priority
 * User needs to log in (based on URL)
 */
export class LoginCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user, location } = context;
    return !user.loggedIn && (user.authMode === 'login' || _.includes(location.pathname, 'login'));
  }

  viewToShow(context) {
    return 'login';
  }


  getCaseName() {
    return 'LoginCase';
  }
}

/**
 * Gift Card Login Case - Medium Priority
 * User needs to log in to access gifts
 */
export class GiftCardLoginCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user, app } = context;
    return !user.loggedIn && app.loginType === LOGIN_TYPES.GIFTS;
  }

  viewToShow(context) {
    return 'login';
  }


  getCaseName() {
    return 'GiftCardLoginCase';
  }
}

/**
 * Scan Login Case - Medium Priority
 * User needs to log in to access scan functionality
 */
export class ScanLoginCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user, app } = context;
    return !user.loggedIn && app.loginType === LOGIN_TYPES.SCAN;
  }

  viewToShow(context) {
    return 'login';
  }


  getCaseName() {
    return 'ScanLoginCase';
  }
}

/**
 * Native App Signup Case - Low Priority
 * Native app requesting signup on homepage
 */
export class NativeAppSignupCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user, location, app } = context;
    const params = getParams(location);
    const isNative = typeof window !== 'undefined' && window.isNativeApp;
    
    return !user.loggedIn && 
           (params.openSignup || (isNative && location.pathname === '/')) &&
           (params.openSignup || app.isMobile);
  }

  viewToShow(context) {
    return 'signup';
  }


  getCaseName() {
    return 'NativeAppSignupCase';
  }
}

/**
 * Default Login Case - Lowest Priority
 * Fallback case for general login needs
 * Only triggers when explicitly opened (not automatically after logout)
 */
export class DefaultLoginCase extends AuthDialogInterface {
  shouldShowDialog(context) {
    const { user, app } = context;
    // Only show if user is not logged in AND auth modal/view is explicitly open
    // This prevents automatic showing after logout
    console.log('DefaultLoginCase', user.loggedIn, app.authModalOpen, app.mobileAuthViewOpen);
    return !user.loggedIn && (app.authModalOpen || app.mobileAuthViewOpen);
  }

  viewToShow(context) {
    return 'login';
  }

  getCaseName() {
    return 'DefaultLoginCase';
  }
}

/**
 * Auth Case Manager - Manages all auth cases and determines which one to use
 * Cases are ordered by priority (first in list = highest priority)
 */
export class AuthCaseManager {
  constructor() {
    // Cases ordered by priority (highest to lowest)
    // First case in the list has the highest priority
    this.cases = [
      new MissingEmailCase(),        // Highest priority
      new PrivacyConsentCase(),      // High priority
      new VerifyCodeCase(),          // High priority
      new SignupCase(),              // Medium priority
      new LoginCase(),               // Medium priority
      new GiftCardLoginCase(),       // Medium priority
      new ScanLoginCase(),           // Medium priority
      new NativeAppSignupCase(),     // Low priority
      new DefaultLoginCase(),        // Lowest priority
    ];
  }

  /**
   * Determines if any auth dialog should be shown
   * @param {Object} context - The application context
   * @returns {boolean} - Whether any dialog should be shown
   */
  shouldShowAnyDialog(context) {
    return this.cases.some(authCase => authCase.shouldShowDialog(context));
  }

  /**
   * Gets the highest priority case that should show a dialog
   * @param {Object} context - The application context
   * @returns {AuthDialogInterface|null} - The auth case to use, or null if none
   */
  getActiveCase(context) {
    return this.cases.find(authCase => authCase.shouldShowDialog(context)) || null;
  }

  /**
   * Gets the view type for the active case
   * @param {Object} context - The application context
   * @returns {string|null} - The view type to show, or null if none
   */
  getActiveViewType(context) {
    const activeCase = this.getActiveCase(context);
    return activeCase ? activeCase.viewToShow(context) : null;
  }

  /**
   * Gets debug information about all cases
   * @param {Object} context - The application context
   * @returns {Array} - Array of case information for debugging
   */
  getDebugInfo(context) {
    return this.cases.map((authCase, index) => ({
      name: authCase.getCaseName(),
      priority: this.cases.length - index, // Higher index = lower priority
      shouldShow: authCase.shouldShowDialog(context),
      viewType: authCase.shouldShowDialog(context) ? authCase.viewToShow(context) : null,
    }));
  }
}
